/**
 * ImagePromptEditorPage - Mobile
 * Dedicated page to view/edit AI image prompt in logical blocks and regenerate.
 */
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import './ImagePromptEditorPage.css';
import '../MobileAIStorage/ProjectDetailPage.css';
import useApi from '../../../hooks/useApi';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import Skeleton from '../../effects/Skeleton';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import ImageGalleryPage from '../MobileImageGallery/ImageGalleryPage';
import useLanguage from '../../../hooks/useLanguage';
import { API_CONFIG } from '../../../config/api';
import AIRegenerationModal from './AIRegenerationModal';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';

// Simple icons reused inline to avoid external deps
const BackIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AIIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.56791 18.8123V17.9563H1.71194C1.23925 17.9563 0.856061 17.573 0.855972 17.1003C0.855972 16.6276 1.2392 16.2444 1.71194 16.2444H2.56791V15.3884C2.56791 14.9157 2.95119 14.5325 3.42389 14.5324C3.89666 14.5324 4.27986 14.9156 4.27986 15.3884V16.2444H5.13583C5.6086 16.2444 5.9918 16.6276 5.9918 17.1003C5.99171 17.573 5.60855 17.9563 5.13583 17.9563H4.27986V18.8123C4.27983 19.285 3.89664 19.6683 3.42389 19.6683C2.95121 19.6682 2.56794 19.285 2.56791 18.8123ZM10.4767 4.80819C10.6996 4.77883 10.927 4.79315 11.1455 4.85199H11.1446C11.5422 4.95852 11.8004 5.23185 11.9627 5.47207C12.1242 5.71114 12.2481 6.00066 12.3529 6.29619C12.5623 6.88697 12.7703 7.72584 13.0287 8.7586L13.1272 9.12687C13.1277 9.1282 13.1278 9.12959 13.1282 9.13085C13.1298 9.13146 13.1315 9.13219 13.1332 9.13284C13.2028 9.15873 13.3021 9.18814 13.4955 9.24332L14.7028 9.59467C15.0616 9.70418 15.3747 9.80788 15.6344 9.91118C15.8954 10.015 16.1552 10.1383 16.3729 10.2984C16.5953 10.4619 16.832 10.7075 16.9343 11.0667L16.9632 11.1822C17.0206 11.4541 17.0108 11.7369 16.9343 12.0053C16.832 12.3646 16.5953 12.6102 16.3729 12.7737C16.1552 12.9337 15.8954 13.0571 15.6344 13.1609C15.3747 13.2642 15.0616 13.3679 14.7028 13.4774L13.4955 13.8287C13.3017 13.884 13.2026 13.9134 13.1332 13.9392C13.1316 13.9398 13.1297 13.9396 13.1282 13.9402C13.1277 13.9417 13.1277 13.9436 13.1272 13.9452C13.1037 14.0159 13.0776 14.1158 13.0287 14.3125V14.3135C12.7703 15.3462 12.5623 16.1851 12.3529 16.7759C12.2482 17.0712 12.124 17.36 11.9627 17.599C11.8004 17.8393 11.5424 18.1135 11.1446 18.2201L11.1436 18.2191C10.8524 18.2974 10.5448 18.2978 10.2537 18.2191V18.2181C9.85746 18.1108 9.59965 17.839 9.43758 17.599C9.27623 17.36 9.15315 17.0703 9.04842 16.7749C8.83905 16.1842 8.62996 15.3461 8.3716 14.3135V14.3125C8.32287 14.1171 8.29666 14.0169 8.27306 13.9462C8.27238 13.9441 8.27171 13.9421 8.27107 13.9402C8.26992 13.9398 8.26831 13.9397 8.26709 13.9392C8.19746 13.9134 8.09824 13.8838 7.9048 13.8287H7.9038C7.01716 13.5751 6.28619 13.3673 5.76686 13.1609C5.50565 13.057 5.24523 12.9337 5.02734 12.7737C4.80497 12.6104 4.56776 12.3654 4.46499 12.0063V12.0053C4.37762 11.6987 4.37762 11.3734 4.46499 11.0667V11.0657L4.50978 10.9364C4.62696 10.6467 4.83277 10.4413 5.02734 10.2984C5.24523 10.1383 5.50565 10.015 5.76686 9.91118C6.28619 9.70471 7.01714 9.49701 7.9038 9.24332L8.26709 9.13284C8.26847 9.13233 8.26978 9.13134 8.27107 9.13085C8.27152 9.12954 8.2726 9.12826 8.27306 9.12687C8.29659 9.05618 8.32263 8.95638 8.3716 8.7596V8.7586L8.72792 7.36118C8.83852 6.94875 8.94375 6.5915 9.04842 6.29619C9.15315 6.00074 9.27615 5.71108 9.43758 5.47207C9.59972 5.23206 9.85767 4.95878 10.2547 4.85199L10.4767 4.80819Z" fill="currentColor"/>
  </svg>
);

const CreativityLowIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" fill="currentColor"/>
  </svg>
);

const CreativityHighIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" fill="currentColor"/>
    <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
  </svg>
);

const PlusIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const AILoadingOverlay = ({ text = '' }) => (
  <div className="ai-loading-container prompt-editor-ai-loading">
    <div className="ai-loading-dim" />
    <div className="ai-loading-content">
      <div className="ai-loading-icon-container">
        <AIIcon size={24} />
        <div className="ai-loading-pulse" />
      </div>
      <div className="ai-loading-text">
        {text}
        <span className="ai-loading-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </div>
    </div>
    <div className="ai-loading-background" />
  </div>
);

const HTTP_PROTOCOL_REGEX = /^https?:\/\//i;
const BLOB_URL_REGEX = /^blob:/i;
const DATA_URL_REGEX = /^data:/i;
const PROMPT_MIN_LENGTH = 10;
const PROMPT_MAX_LENGTH = 1000;

const PROMPT_KEY_ALIASES = {
  soggetto: 'subject',
  subject: 'subject',
  composizione: 'composition',
  composition: 'composition',
  stile: 'style',
  style: 'style',
  camera: 'camera',
  luce: 'lighting',
  lighting: 'lighting',
  illuminazione: 'lighting',
  tono: 'tone',
  tone: 'tone',
  qualita: 'quality',
  qualità: 'quality',
  quality: 'quality',
  colori: 'color_palette',
  palette: 'color_palette',
  'palette_colori': 'color_palette',
  mood: 'mood',
  atmosfera: 'mood',
  notes: 'additional_notes',
  note: 'additional_notes',
  'note_aggiuntive': 'additional_notes',
  'altri_dettagli': 'additional_notes',
  'additional notes': 'additional_notes',
  'additional_notes': 'additional_notes',
  'detagli': 'additional_notes',
  metadata: 'metadata',
  instructions: 'instructions'
};

const PROMPT_JSON_KEY_ORDER = [
  'subject',
  'composition',
  'style',
  'camera',
  'lighting',
  'mood',
  'tone',
  'quality',
  'color_palette',
  'additional_notes'
];

const RESERVED_PROMPT_JSON_KEYS = new Set(['metadata', 'reference_images', 'instructions']);

const PROMPT_LABEL_DEFAULTS = {
  en: {
    subject: 'Subject',
    composition: 'Composition',
    style: 'Style',
    camera: 'Camera',
    lighting: 'Lighting',
    mood: 'Mood',
    tone: 'Tone',
    quality: 'Quality',
    color_palette: 'Color palette',
    additional_notes: 'Additional notes'
  },
  it: {
    subject: 'Soggetto',
    composition: 'Composizione',
    style: 'Stile',
    camera: 'Camera',
    lighting: 'Illuminazione',
    mood: 'Atmosfera',
    tone: 'Tono',
    quality: 'Qualità',
    color_palette: 'Palette colori',
    additional_notes: 'Note aggiuntive'
  }
};

const BLOCK_PLACEHOLDER_EXAMPLES = {
  subject: 'Es: Un cane con le gambe di coccodrillo',
  composition: 'Es: Inquadratura dall’alto su un picnic un po’ pasticciato',
  style: 'Es: Stile fumetto anni 90, colori super saturi',
  camera: 'Es: Scatto fish-eye da skater che fa una piega',
  lighting: 'Es: Luci al neon da discoteca anni 80',
  mood: 'Es: Atmosfera festa sorpresa con amici che ridono',
  tone: 'Es: Tono ironico ma curato, zero formalità',
  quality: 'Es: Alta definizione ma niente posa da catalogo',
  color_palette: 'Es: Palette gelato pistacchio, fragola e limone',
  additional_notes: 'Es: Niente gatti volanti stavolta (forse)',
  default: 'Es: Aggiungi qui cosa vuoi vedere generato'
};

const normalizeLabelToKey = (label, fallbackIndex = 0) => {
  if (!label) {
    return `section_${fallbackIndex + 1}`;
  }

  const normalized = label
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_');

  if (!normalized) {
    return `section_${fallbackIndex + 1}`;
  }

  return PROMPT_KEY_ALIASES[normalized] || normalized;
};

const parsePromptBlock = (block) => {
  const trimmed = typeof block === 'string' ? block.trim() : '';
  if (!trimmed) {
    return { label: '', value: '' };
  }

  const match = /^\s*([^:]{1,80}):\s*(.*)$/.exec(trimmed);
  if (match) {
    return {
      label: match[1].trim(),
      value: match[2].trim()
    };
  }

  return {
    label: '',
    value: trimmed
  };
};

const clampCreativity = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return undefined;
  }
  return Math.min(1, Math.max(0, parseFloat(numeric.toFixed(2))));
};

const buildPromptJsonFromBlocks = (blocks = [], options = {}) => {
  const {
    normalizedRatio,
    quality,
    tone,
    creativity,
    simplePrompt,
    referenceImages = [],
    mode,
    baseReferenceIncluded = false,
    includeReferenceStats = false
  } = options;

  const promptJson = {};

  blocks.forEach((block, index) => {
    const { label, value } = parsePromptBlock(block);
    if (!value) {
      return;
    }
    const key = normalizeLabelToKey(label || `section_${index + 1}`, index);
    if (!key || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    promptJson[key] = value;
  });

  const metadata = {};
  if (normalizedRatio) {
    metadata.aspect_ratio = normalizedRatio;
  }
  if (quality) {
    metadata.quality = quality;
  }
  if (tone) {
    metadata.tone = tone;
  }
  const normalizedCreativity = clampCreativity(creativity);
  if (normalizedCreativity !== undefined) {
    metadata.creativity = normalizedCreativity;
  }
  if (mode) {
    metadata.mode = mode;
  }
  if (includeReferenceStats) {
    if (typeof baseReferenceIncluded === 'boolean') {
      metadata.base_reference_included = baseReferenceIncluded;
    }
    if (Array.isArray(referenceImages)) {
      metadata.reference_images_supplied = referenceImages.length;
    }
  }

  if (Object.keys(metadata).length > 0) {
    promptJson.metadata = metadata;
  }

  if (Array.isArray(referenceImages) && referenceImages.length > 0) {
    promptJson.reference_images = referenceImages;
  }

  if (simplePrompt) {
    promptJson.instructions = simplePrompt;
  }

  return promptJson;
};

const buildPromptJsonSchema = (promptJson = {}) => {
  const schema = {};
  Object.keys(promptJson).forEach((key) => {
    if (RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    schema[key] = null;
  });
  return Object.keys(schema).length > 0 ? schema : null;
};

const buildBlocksFromPromptJson = (promptJson, resolveLabel) => {
  if (!promptJson || typeof promptJson !== 'object') {
    return [];
  }

  const blocks = [];
  const visited = new Set();

  const appendBlock = (key) => {
    if (!key || visited.has(key) || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }

    const value = promptJson[key];
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      return;
    }

    let content = value;
    if (typeof value === 'object') {
      try {
        content = JSON.stringify(value);
      } catch (error) {
        content = String(value);
      }
    }

    const label = resolveLabel ? resolveLabel(key) : key;
    blocks.push(`${label}: ${content}`);
    visited.add(key);
  };

  PROMPT_JSON_KEY_ORDER.forEach(appendBlock);

  Object.keys(promptJson).forEach((key) => {
    appendBlock(key);
  });

  return blocks;
};

const resolveReferenceCandidate = (rawValue) => {
  const value = typeof rawValue === 'string' ? rawValue.trim() : '';

  if (!value) {
    return { url: null, reason: 'empty', value };
  }

  if (BLOB_URL_REGEX.test(value)) {
    return { url: null, reason: 'blob', value };
  }

  if (DATA_URL_REGEX.test(value)) {
    return { url: null, reason: 'data', value };
  }

  if (HTTP_PROTOCOL_REGEX.test(value)) {
    return { url: value, reason: null, value };
  }

  try {
    const resolved = new URL(value, API_CONFIG?.BASE_URL || undefined);
    if (resolved && HTTP_PROTOCOL_REGEX.test(resolved.href)) {
      return { url: resolved.href, reason: null, value };
    }
  } catch (error) {
    // Fallback to window origin in browser environments when API base fails
    if (typeof window !== 'undefined' && window.location) {
      try {
        const fallbackResolved = new URL(value, window.location.origin);
        if (HTTP_PROTOCOL_REGEX.test(fallbackResolved.href)) {
          return { url: fallbackResolved.href, reason: null, value };
        }
      } catch (fallbackError) {
        console.warn('[ImagePromptEditorPage] Failed to resolve reference URL with window origin', {
          value,
          error: fallbackError
        });
      }
    }

    console.warn('[ImagePromptEditorPage] Failed to resolve reference URL with API base', {
      value,
      error
    });
  }

  return { url: null, reason: 'invalid', value };
};

const gcd = (a, b) => {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const temp = y;
    y = x % y;
    x = temp;
  }
  return x || 1;
};

// Utility: split prompt into blocks
const splitPromptIntoBlocks = (prompt) => {
  if (!prompt) return [];
  // Prefer newline separation if present
  const byLines = prompt.split('\n').map(p => p.trim()).filter(Boolean);
  if (byLines.length > 1) return byLines;
  // Then semicolons
  const bySemi = prompt.split(';').map(p => p.trim()).filter(Boolean);
  if (bySemi.length > 1) return bySemi;
  // Then sentence boundaries
  const bySentence = prompt.split(/(?<=[.!?])\s+/).map(p => p.trim()).filter(Boolean);
  if (bySentence.length > 1) return bySentence;
  // Fallback single block
  return [prompt.trim()];
};

const ImagePromptEditorPage = ({
  post,
  imageIndex = 0,
  onBack,
  onRegenerate,
  isLoading = false
}) => {
  const { t, language } = useLanguage();
  const { notify, confirm } = useMobileAlert();
  const apiClient = useApi();
  const fallbackImageName = useMemo(
    () => t('postReview.imageEditor.fallback.image', { index: imageIndex + 1 }),
    [t, imageIndex]
  );
  const fallbackReferenceName = useMemo(
    () => t('postReview.imageEditor.fallback.reference'),
    [t]
  );
  const languageCode = useMemo(() => (
    typeof language === 'string' && language
      ? language.split('-')[0]?.toLowerCase() || 'en'
      : 'en'
  ), [language]);

  const labelDefaults = useMemo(() => (
    PROMPT_LABEL_DEFAULTS[languageCode] || PROMPT_LABEL_DEFAULTS.en
  ), [languageCode]);

  const buildEmptyBlocksFromMock = useCallback((idx) => {
    const title = post?.title || t('postReview.imageEditor.defaults.titleFallback', { index: idx + 1 });
    const brand = post?.company_name || post?.brand || t('postReview.imageEditor.defaults.brandFallback');
    const lines = t('postReview.imageEditor.defaults.prompt', {
      title,
      brand,
      returnObjects: true
    });

    if (!Array.isArray(lines) || !lines.length) {
      return [''];
    }

    return lines.map((line, lineIdx) => {
      const { label } = parsePromptBlock(line);
      const safeLabel = label || t('postReview.imageEditor.blocks.defaultLabel', { defaultValue: `Blocco ${lineIdx + 1}` });
      return `${safeLabel}: `;
    });
  }, [post, t]);

const buildBlockPlaceholder = useCallback((label, idx) => {
  const normalizedKey = normalizeLabelToKey(label, idx);
  const example = BLOCK_PLACEHOLDER_EXAMPLES[normalizedKey] || BLOCK_PLACEHOLDER_EXAMPLES.default;
  return example;
}, [t]);
  // Try to read stored prompt fields commonly used in mobile components
  const initialPrompt = useMemo(() => {
    const candidates = [
      post?.imagesPrompts?.[imageIndex],
      post?.revised_prompt,
      post?.original_prompt,
      post?.ai_prompt,
      ''
    ];
    return (candidates.find(Boolean) || '').trim();
  }, [post, imageIndex]);

  const [blocks, setBlocks] = useState(() => {
    const b = splitPromptIntoBlocks(initialPrompt);
    return b.length ? b : buildEmptyBlocksFromMock(imageIndex);
  });
  const [creativity, setCreativity] = useState(0.5); // 0..1
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [customWidth, setCustomWidth] = useState('1024');
  const [customHeight, setCustomHeight] = useState('1024');
  const [quality, setQuality] = useState('standard');
  const [tone, setTone] = useState('modern');
  const [loading, setLoading] = useState(false);

  // New states for simple/pro mode and prompt image (single image)
  const [mode, setMode] = useState('simple'); // 'simple' or 'pro'
  const [simplePromptText, setSimplePromptText] = useState('');
  const [promptImage, setPromptImage] = useState(null); // Single image object or null
  const [showImageGallery, setShowImageGallery] = useState(false);
  const promptImageObjectUrlRef = useRef(null);
  const promptJsonSignatureRef = useRef(null);
  const [useCurrentImageAsBase, setUseCurrentImageAsBase] = useState(false);
  const [showPromptBlocksModal, setShowPromptBlocksModal] = useState(false);
  const [isRegeneratingBlocks, setIsRegeneratingBlocks] = useState(false);
  const lastGeneratedImageRef = useRef(null);
  const generationPendingRef = useRef(false);

  const currentMediaItem = useMemo(() => {
    if (!post || !Array.isArray(post.mediaArray)) {
      return null;
    }
    const item = post.mediaArray[imageIndex];
    if (item) {
      return item;
    }
    return post.mediaArray[0] || null;
  }, [post, imageIndex]);

  const currentImage = useMemo(() => {
    if (!post || !Array.isArray(post.images)) return null;
    const image = post.images[imageIndex] ?? post.images[0] ?? null;
    return image || null;
  }, [post, imageIndex]);

  const currentImageUrl = useMemo(() => {
    const fromImages = (() => {
      if (!currentImage) return null;
      if (typeof currentImage === 'string') return currentImage;
      return currentImage.url || currentImage.imageUrl || currentImage.src || null;
    })();

    if (fromImages) {
      return fromImages;
    }

    if (!currentMediaItem) {
      return null;
    }

    return (
      currentMediaItem.url ||
      currentMediaItem.previewUrl ||
      currentMediaItem.imageUrl ||
      currentMediaItem.src ||
      null
    );
  }, [currentImage, currentMediaItem]);

  const currentImageName = useMemo(() => {
    if (typeof currentImage === 'object' && currentImage) {
      return currentImage.name || currentImage.title || currentImage.alt || fallbackImageName;
    }
    return fallbackImageName;
  }, [currentImage, fallbackImageName]);

  const postId = useMemo(() => {
    if (!post) return null;
    const originalDataId = post.originalData?.id || post.originalData?._id;
    return post.id || post._id || post.postId || originalDataId || null;
  }, [post]);

  const editingSessionKey = useMemo(
    () => `${postId || 'unknown'}-${imageIndex}`,
    [postId, imageIndex]
  );

  const isModifyMode = mode === 'simple';
  const baseImageStatusLabel = isModifyMode
    ? t('postReview.imageEditor.base.statusOn', { defaultValue: 'In uso' })
    : t('postReview.imageEditor.base.statusOff', { defaultValue: 'Non in uso' });

  const resolveLabelForKey = useCallback((key) => {
    if (!key) {
      return '';
    }

    const normalized = key.toString().toLowerCase();
    const fallbackDefault = labelDefaults[normalized] || normalized
      .split('_')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    const translationKey = normalized.replace(/[^a-z0-9]+/g, '_');

    return t(`postReview.imageEditor.promptLabels.${translationKey}`, {
      defaultValue: fallbackDefault || key
    });
  }, [labelDefaults, t]);

  useEffect(() => {
    if (!currentImageUrl && useCurrentImageAsBase) {
      setUseCurrentImageAsBase(false);
      return;
    }

    if (!currentImageUrl) {
      return;
    }

    const desiredState = isModifyMode;
    if (useCurrentImageAsBase !== desiredState) {
      setUseCurrentImageAsBase(desiredState);
    }
  }, [currentImageUrl, isModifyMode, useCurrentImageAsBase]);

  useEffect(() => {
    if (!editingSessionKey) {
      return;
    }

    const resolvedBlocks = (() => {
      const primary = splitPromptIntoBlocks(initialPrompt);
      if (primary.length) {
        return primary;
      }
      const fallback = buildEmptyBlocksFromMock(imageIndex);
      return fallback.length ? fallback : [''];
    })();
    setBlocks(resolvedBlocks);

    setSimplePromptText('');

    if (promptImageObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(promptImageObjectUrlRef.current);
      } catch (error) {
        console.warn('[ImagePromptEditorPage] Failed to revoke object URL during session reset', error);
      }
      promptImageObjectUrlRef.current = null;
    }
    setPromptImage(null);

    promptJsonSignatureRef.current = null;
    lastGeneratedImageRef.current = null;
    generationPendingRef.current = false;
    setHasGeneratedImage(false);

    setMode('simple');
    setCreativity(0.5);
    setQuality('standard');
    setTone('modern');
    setAspectRatio('1:1');
    setCustomWidth('1024');
    setCustomHeight('1024');
    setUseCurrentImageAsBase(false);
  }, [buildEmptyBlocksFromMock, editingSessionKey, imageIndex, initialPrompt]);

  const existingPromptJson = useMemo(() => {
    const parseCandidate = (candidate) => {
      if (!candidate) {
        return null;
      }

      if (typeof candidate === 'string') {
        try {
          return JSON.parse(candidate);
        } catch (error) {
          console.warn('[ImagePromptEditorPage] Unable to parse prompt_json string', error);
          return null;
        }
      }

      if (typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate;
      }

      return null;
    };

    const mediaFile = Array.isArray(post?.media_files) ? post.media_files[imageIndex] : null;
    const candidates = [
      currentMediaItem?.prompt_json,
      currentMediaItem?.metadata?.prompt_json,
      currentMediaItem?.ai_metadata?.prompt_json,
      currentMediaItem?.generation_metadata?.prompt_json,
      mediaFile?.prompt_json,
      mediaFile?.metadata?.prompt_json,
      post?.prompt_json,
      post?.promptJson,
      Array.isArray(post?.images_prompt_json) ? post.images_prompt_json[imageIndex] : null,
      Array.isArray(post?.imagesPromptsJson) ? post.imagesPromptsJson[imageIndex] : null,
      post?.regeneration_metadata?.prompt_json,
      Array.isArray(post?.regeneration_metadata?.generated_images)
        ? post.regeneration_metadata.generated_images[imageIndex]?.prompt_json
        : null,
      post?.__pendingRegeneratedResponse?.regeneration_metadata?.prompt_json,
      Array.isArray(post?.__pendingRegeneratedResponse?.regeneration_metadata?.generated_images)
        ? post.__pendingRegeneratedResponse.regeneration_metadata.generated_images[imageIndex]?.prompt_json
        : null,
      post?.__pendingRegeneratedResponse?.regenerated_content?.prompt_json
    ];

    for (const candidate of candidates) {
      const parsed = parseCandidate(candidate);
      if (parsed) {
        return parsed;
      }
    }

    return null;
  }, [currentMediaItem, imageIndex, post]);

  useEffect(() => {
    if (!existingPromptJson) {
      return;
    }

    let signature = null;
    try {
      signature = JSON.stringify(existingPromptJson);
    } catch (error) {
      console.warn('[ImagePromptEditorPage] Unable to serialize incoming prompt_json', error);
    }

    if (signature && promptJsonSignatureRef.current === signature) {
      return;
    }

    if (signature) {
      promptJsonSignatureRef.current = signature;
    }

    const derivedBlocks = buildBlocksFromPromptJson(existingPromptJson, resolveLabelForKey);
    if (derivedBlocks.length > 0) {
      setBlocks(derivedBlocks);
    }

    // Do not prefill simple prompt textarea with previous instructions;
    // only structured blocks and metadata are restored

    const metadata = existingPromptJson.metadata || {};
    if (typeof metadata.quality === 'string' && metadata.quality) {
      setQuality(metadata.quality);
    }
    if (typeof metadata.tone === 'string' && metadata.tone) {
      setTone(metadata.tone);
    }
    if (metadata.creativity !== undefined) {
      const normalizedCreativity = clampCreativity(metadata.creativity);
      if (normalizedCreativity !== undefined) {
        setCreativity(normalizedCreativity);
      }
    }

    const ratioValue = metadata.aspect_ratio || metadata.ratio;
    const dimensions = metadata.dimensions || metadata.dimension;
    const width = metadata.width || (dimensions && (dimensions.width || dimensions.w));
    const height = metadata.height || (dimensions && (dimensions.height || dimensions.h));

    if (typeof ratioValue === 'string') {
      if (/^\d+\s*:\s*\d+$/.test(ratioValue)) {
        setAspectRatio(ratioValue.replace(/\s+/g, ''));
      } else if (/^\d+\s*x\s*\d+$/i.test(ratioValue)) {
        const [w, h] = ratioValue.toLowerCase().split('x').map(part => part.trim());
        if (w && h) {
          setAspectRatio('custom');
          setCustomWidth(w);
          setCustomHeight(h);
        }
      }
    } else if (width && height) {
      setAspectRatio('custom');
      setCustomWidth(String(width));
      setCustomHeight(String(height));
    }
  }, [existingPromptJson, resolveLabelForKey]);

  const handleBlockChange = useCallback((idx, value) => {
    setBlocks(prev => prev.map((b, i) => (i === idx ? value : b)));
  }, []);

  const handleAddBlock = useCallback(() => {
    setBlocks(prev => [...prev, '']);
  }, []);

  const handleRemoveBlock = useCallback((idx) => {
    setBlocks(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const buildMetadataHeader = useCallback(() => {
    const dimensions = aspectRatio === 'custom' ? `${customWidth}x${customHeight}` : aspectRatio;
    return t('postReview.imageEditor.defaults.metadata', {
      ratio: dimensions,
      quality,
      tone,
      creativity
    });
  }, [aspectRatio, customWidth, customHeight, creativity, quality, tone, t]);

  const normalizePromptField = useCallback((value, { max = PROMPT_MAX_LENGTH } = {}) => {
    const trimmed = (value || '').trim();

    if (!trimmed) {
      return '';
    }

    if (trimmed.length > max) {
      return trimmed.slice(0, max);
    }

    return trimmed;
  }, []);

  const buildPrompt = useCallback(() => {
    const joined = blocks.map(b => b.trim()).filter(Boolean).join('\n');
    const header = buildMetadataHeader();
    return `${header}\n\n${joined}`.trim();
  }, [blocks, buildMetadataHeader]);

  const normalizeAspectRatio = useCallback(() => {
    if (aspectRatio === 'custom') {
      const width = parseInt(customWidth, 10);
      const height = parseInt(customHeight, 10);
      if (width > 0 && height > 0) {
        const divisor = gcd(width, height);
        const normalizedWidth = Math.round(width / divisor);
        const normalizedHeight = Math.round(height / divisor);
        if (normalizedWidth > 0 && normalizedHeight > 0) {
          return `${normalizedWidth}:${normalizedHeight}`;
        }
      }
      return '4:5';
    }

    const allowed = new Set(['1:1', '4:5', '5:4', '16:9', '9:16', '3:4', '4:3']);
    if (allowed.has(aspectRatio)) {
      return aspectRatio;
    }

    if (aspectRatio === '21:9') {
      return '16:9';
    }

    return '4:5';
  }, [aspectRatio, customWidth, customHeight]);

  const normalizedRatio = useMemo(() => normalizeAspectRatio(), [normalizeAspectRatio]);
  const promptModeType = useMemo(() => (mode === 'simple' ? 'modify' : 'generate'), [mode]);
  const isGenerateMode = promptModeType === 'generate';
  const trimmedSimplePrompt = useMemo(() => simplePromptText.trim(), [simplePromptText]);
  const [hasGeneratedImage, setHasGeneratedImage] = useState(false);

  useEffect(() => {
    if (generationPendingRef.current && currentImageUrl) {
      lastGeneratedImageRef.current = currentImageUrl;
      setHasGeneratedImage(true);
      generationPendingRef.current = false;
      return;
    }

    if (isGenerateMode && lastGeneratedImageRef.current) {
      setHasGeneratedImage(true);
      return;
    }

    setHasGeneratedImage(false);
  }, [currentImageUrl, isGenerateMode]);

  const displayImageUrl = isGenerateMode
    ? (hasGeneratedImage ? (currentImageUrl || lastGeneratedImageRef.current) : null)
    : currentImageUrl;
  const shouldRenderCurrentImage = isGenerateMode || Boolean(currentImageUrl) || Boolean(lastGeneratedImageRef.current);

  const derivedPromptJson = useMemo(() => buildPromptJsonFromBlocks(blocks, {
    normalizedRatio,
    quality,
    tone,
    creativity,
    simplePrompt: trimmedSimplePrompt,
    referenceImages: [],
    mode: promptModeType
  }), [
    blocks,
    normalizedRatio,
    quality,
    tone,
    creativity,
    trimmedSimplePrompt,
    promptModeType
  ]);

  const hasPromptJsonContent = useMemo(() => {
    if (!derivedPromptJson || typeof derivedPromptJson !== 'object') {
      return false;
    }
    return Object.keys(derivedPromptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && derivedPromptJson[key]);
  }, [derivedPromptJson]);

  const buildUserPrompt = useCallback(() => {
    const structuredPrompt = buildPrompt().trim();

    if (mode === 'simple') {
      return simplePromptText.trim();
    }

    return structuredPrompt;
  }, [buildPrompt, mode, simplePromptText]);

  const prepareReferenceImages = useCallback(async () => {
    if (isGenerateMode) {
      return {
        references: [],
        baseReferenceIncluded: false,
        warnings: []
      };
    }

    const seenReferences = new Set();
    let baseReference = null;
    const extraReferences = [];
    const warnings = [];

    const appendExtraReference = (url) => {
      if (url && !seenReferences.has(url) && url !== baseReference) {
        seenReferences.add(url);
        extraReferences.push(url);
      }
    };

    const attemptAddBaseReference = (candidate, source) => {
      if (!candidate) {
        return;
      }

      const { url, reason } = resolveReferenceCandidate(candidate);
      if (url && !baseReference) {
        baseReference = url;
        seenReferences.add(url);
      } else if (reason && reason !== 'empty') {
        warnings.push({ source, reason, value: candidate });
      }
    };

    const attemptAddReference = (candidate, source) => {
      if (source === 'base') {
        attemptAddBaseReference(candidate, source);
        return;
      }

      if (!candidate) {
        return;
      }

      const { url, reason } = resolveReferenceCandidate(candidate);
      if (url) {
        appendExtraReference(url);
      } else if (reason && reason !== 'empty') {
        warnings.push({ source, reason, value: candidate });
      }
    };

    // Always try to include the current image as base in modify mode
    const mediaFile = Array.isArray(post?.media_files) ? post.media_files[imageIndex] : null;
    const fallbackMediaFile = Array.isArray(post?.media_files) ? post.media_files[0] : null;
    const baseCandidates = [
      currentImageUrl,
      currentMediaItem?.url,
      currentMediaItem?.previewUrl,
      currentMediaItem?.imageUrl,
      currentMediaItem?.src,
      mediaFile?.url,
      mediaFile?.previewUrl,
      mediaFile?.imageUrl,
      mediaFile?.src,
      fallbackMediaFile?.url,
      fallbackMediaFile?.previewUrl,
      fallbackMediaFile?.imageUrl,
      fallbackMediaFile?.src
    ];

    baseCandidates.forEach(candidate => attemptAddReference(candidate, 'base'));

    if (!baseReference && currentMediaItem?.type === 'file') {
      warnings.push({ source: 'base', reason: 'unuploaded-file' });
    }

    if (promptImage) {
      attemptAddReference(promptImage.url, 'prompt');
      attemptAddReference(promptImage.previewUrl, 'prompt');

      if (promptImage.file && (!promptImage.url || BLOB_URL_REGEX.test(promptImage.url))) {
        warnings.push({ source: 'prompt', reason: 'unuploaded-file' });
      }
    }

    const references = baseReference
      ? [baseReference, ...extraReferences.filter(ref => ref !== baseReference)]
      : extraReferences;

    return {
      references,
      baseReferenceIncluded: Boolean(baseReference),
      warnings
    };
  }, [currentImageUrl, currentMediaItem, promptImage, isGenerateMode, post, imageIndex]);

  const handleRegenerateAll = useCallback(async () => {
    if (!postId) {
      notify({
        variant: 'error',
        title: t('postReview.imageEditor.alerts.missingPostId')
      });
      return;
    }

    const userPrompt = buildUserPrompt();
    const trimmedUserPrompt = userPrompt.trim();
    if (!trimmedUserPrompt) {
      notify({
        variant: 'warning',
        title: t('postReview.imageEditor.alerts.missingPrompt')
      });
      return;
    }

    if (trimmedUserPrompt.length < PROMPT_MIN_LENGTH) {
      notify({
        variant: 'warning',
        title: t('postReview.imageEditor.alerts.shortPrompt', { defaultValue: 'Il prompt deve contenere almeno 10 caratteri.' })
      });
      return;
    }

    let normalizedUserPrompt = normalizePromptField(trimmedUserPrompt, { max: PROMPT_MAX_LENGTH });
    let normalizedPromptText = null;

    if (!isModifyMode) {
      const promptText = (buildPrompt() || '').trim();
      if (promptText.length >= PROMPT_MIN_LENGTH) {
        normalizedPromptText = normalizePromptField(promptText, {
          max: PROMPT_MAX_LENGTH
        });
      }
    }

    if (normalizedUserPrompt.length > PROMPT_MAX_LENGTH) {
      normalizedUserPrompt = normalizedUserPrompt.slice(0, PROMPT_MAX_LENGTH);
    }

    setLoading(true);
    generationPendingRef.current = true;

    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }

      const { references: referenceImages, baseReferenceIncluded, warnings } = await prepareReferenceImages();

      if (warnings.length) {
        warnings.forEach((warning) => {
          console.warn('[ImagePromptEditorPage] Skipping reference image for AI request', warning);
        });
      }

      if (isModifyMode && !baseReferenceIncluded) {
        throw new Error(`${t('postReview.imageEditor.alerts.error')} (Impossibile usare l'immagine corrente come riferimento AI. Salva il post o seleziona un'immagine già caricata.)`);
      }

      const promptWarnings = warnings.filter(
        (warning) => warning.source === 'prompt' && warning.reason === 'unuploaded-file'
      );

      if (promptWarnings.length) {
        notify({
          variant: 'info',
          title: 'Le immagini di riferimento caricate localmente non possono essere inviate finché non vengono salvate sul server. Verranno ignorate per questa rigenerazione.'
        });
      }

      const payload = (() => {
        if (isModifyMode) {
          const basePayload = {
            prompt_text: normalizedUserPrompt
          };

          if (referenceImages.length) {
            basePayload.reference_images = referenceImages;
          }

          return basePayload;
        }

        const generatePayload = {
          user_prompt: normalizedUserPrompt,
          count: 1,
          ratio: normalizedRatio
        };

        if (normalizedPromptText) {
          generatePayload.prompt_text = normalizedPromptText;
        }

        if (referenceImages.length) {
          generatePayload.reference_images = referenceImages;
        }

        if (hasPromptJsonContent) {
          const promptJson = buildPromptJsonFromBlocks(blocks, {
            normalizedRatio,
            quality,
            tone,
            creativity,
            simplePrompt: trimmedSimplePrompt || undefined,
            referenceImages,
            mode: promptModeType,
            baseReferenceIncluded,
            includeReferenceStats: true
          });

          const promptJsonSchema = buildPromptJsonSchema(promptJson);

          const hasJsonPayload = Object.keys(promptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && promptJson[key]);

          if (hasJsonPayload) {
            generatePayload.prompt_json = promptJson;
          }

          if (promptJsonSchema && hasJsonPayload) {
            generatePayload.prompt_json_schema = promptJsonSchema;
          }
        }

        return generatePayload;
      })();

      const response = await apiClient.post(
        `/api/v1/posts/${postId}/regenerate/image`,
        payload
      );

      if (typeof onRegenerate === 'function') {
        await onRegenerate({
          type: 'image',
          prompt: normalizedUserPrompt,
          imageIndex,
          payload,
          response
        });
      }

      notify({
        variant: 'success',
        title: t('postReview.imageEditor.alerts.success')
      });
    } catch (error) {
      console.error('[ImagePromptEditorPage] Regenerate failed', error);
      const message = error?.message || t('postReview.imageEditor.alerts.error');
      notify({
        variant: 'error',
        title: message
      });
      generationPendingRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [
    apiClient,
    blocks,
    buildPrompt,
    normalizePromptField,
    buildUserPrompt,
    creativity,
    hasPromptJsonContent,
    imageIndex,
    normalizedRatio,
    onRegenerate,
    postId,
    promptModeType,
    prepareReferenceImages,
    quality,
    tone,
    trimmedSimplePrompt,
    t,
    isModifyMode,
    useCurrentImageAsBase,
    notify
  ]);

  const handleGenerateImage = useCallback(async () => {
    if (loading) return;

    const confirmed = await confirm({
      title: t('postReview.imageEditor.alerts.confirm'),
      variant: 'warning',
      confirmText: t('postReview.imageEditor.actions.regenerate', { defaultValue: 'Rigenera' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
    });

    if (confirmed) {
      handleRegenerateAll();
    }
  }, [confirm, handleRegenerateAll, loading, t]);

  const handleClosePromptBlocksModal = useCallback(() => {
    if (isRegeneratingBlocks) {
      return;
    }
    setShowPromptBlocksModal(false);
  }, [isRegeneratingBlocks]);

  const handlePromptBlocksRegeneration = useCallback(async ({ prompt: modalPrompt }) => {
    const trimmedInstructions = (modalPrompt || '').trim();

    if (!postId) {
      notify({
        variant: 'error',
        title: t('postReview.imageEditor.alerts.missingPostId')
      });
      return;
    }

    if (!trimmedInstructions) {
      notify({
        variant: 'warning',
        title: t('postReview.imageEditor.alerts.missingPrompt')
      });
      return;
    }

    setIsRegeneratingBlocks(true);

    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }

      const metadataHeader = buildMetadataHeader();
      const enrichedInstructions = [
        trimmedInstructions,
        metadataHeader
      ].filter(Boolean).join('\n\n');

      const payload = {
        prompt_text: trimmedInstructions
      };

      if (enrichedInstructions) {
        payload.user_prompt = enrichedInstructions;
      }

      if (hasPromptJsonContent) {
        const promptJsonSchema = buildPromptJsonSchema(derivedPromptJson);
        if (promptJsonSchema) {
          payload.prompt_json_schema = promptJsonSchema;
        }
      }

      const response = await apiClient.post(
        '/api/v1/posts/prompt-json',
        payload
      );

      const resolvedPromptJson = response?.prompt_json || null;

      const resolvedPromptText =
        response?.prompt_text ||
        '';

      if (resolvedPromptJson) {
        try {
          promptJsonSignatureRef.current = JSON.stringify(resolvedPromptJson);
        } catch (error) {
          console.warn('[ImagePromptEditorPage] Failed to serialize regenerated prompt_json', error);
        }

        const regeneratedBlocks = buildBlocksFromPromptJson(resolvedPromptJson, resolveLabelForKey);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      } else if (resolvedPromptText) {
        const regeneratedBlocks = splitPromptIntoBlocks(resolvedPromptText);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      }

      // Keep the simple prompt textarea untouched; regenerated instructions appear only in blocks

      setShowPromptBlocksModal(false);
      notify({
        variant: 'success',
        title: t('postReview.imageEditor.alerts.blocksSuccess', { defaultValue: 'Blocchi rigenerati! Ricordati di salvare le modifiche.' })
      });
    } catch (error) {
      console.error('[ImagePromptEditorPage] Prompt blocks regeneration failed', error);
      const message = error?.message || t('postReview.imageEditor.alerts.blocksError', { defaultValue: 'Impossibile rigenerare i blocchi del prompt. Riprova.' });
      notify({
        variant: 'error',
        title: message
      });
    } finally {
      setIsRegeneratingBlocks(false);
    }
  }, [
    apiClient,
    buildMetadataHeader,
    derivedPromptJson,
    hasPromptJsonContent,
    postId,
    resolveLabelForKey,
    t,
    notify
  ]);

  // Trigger image gallery opening
  const triggerPromptImageUpload = useCallback(() => {
    setShowImageGallery(true);
  }, []);

  // Handle image selection from gallery
  const handleImageSelect = useCallback((image) => {
    if (image?.files && image.files.length > 0) {
      const file = image.files[0];
      const previewUrl = URL.createObjectURL(file);

      if (promptImageObjectUrlRef.current) {
        try {
          URL.revokeObjectURL(promptImageObjectUrlRef.current);
        } catch (error) {
          console.warn('[ImagePromptEditorPage] Failed to revoke previous object URL', error);
        }
      }

      promptImageObjectUrlRef.current = previewUrl;

      setPromptImage({
        id: previewUrl,
        url: previewUrl,
        name: file.name || fallbackReferenceName,
        file
      });
    } else if (image && image.url) {
      setPromptImage({
        id: image.id,
        url: image.url,
        name: image.name || fallbackReferenceName
      });
    }
    setShowImageGallery(false);
  }, [fallbackReferenceName]);

  // Handle image deletion
  const handleDeletePromptImage = useCallback(() => {
    if (promptImageObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(promptImageObjectUrlRef.current);
      } catch (error) {
        console.warn('[ImagePromptEditorPage] Failed to revoke object URL on delete', error);
      }
      promptImageObjectUrlRef.current = null;
    }
    setPromptImage(null);
  }, []);

  // Handle back from gallery
  const handleBackFromGallery = useCallback(() => {
    setShowImageGallery(false);
  }, []);

  useEffect(() => () => {
    if (promptImageObjectUrlRef.current) {
      try {
        URL.revokeObjectURL(promptImageObjectUrlRef.current);
      } catch (error) {
        console.warn('[ImagePromptEditorPage] Failed to revoke object URL on unmount', error);
      }
      promptImageObjectUrlRef.current = null;
    }
  }, []);

  // Show ImageGalleryPage if requested
  if (showImageGallery) {
    return (
      <>
        <ImageGalleryPage
          onBack={handleBackFromGallery}
          onImageSelect={handleImageSelect}
        />
        <AIRegenerationModal
          isOpen={showPromptBlocksModal}
          onClose={handleClosePromptBlocksModal}
          type="blocks"
          post={post}
          imageIndex={imageIndex}
          onRegenerate={handlePromptBlocksRegeneration}
          isRegenerating={isRegeneratingBlocks}
        />
      </>
    );
  }

  return (
    <>
      <div className="mobile-ai-storage mobile-ai-prompt-editor">
      {isLoading && (
        <div className="mobile-ai-prompt-editor-skeleton" style={{ padding: 12 }}>
          {/* Header skeleton */}
          <div className="mobile-project-detail-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Skeleton variant="circle" width="28px" height="28px" shimmer />
            <Skeleton width="50%" height="16px" shimmer />
          </div>
          {/* Blocks skeleton */}
          <div style={{ marginTop: 12 }}>
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} style={{ marginBottom: 10 }}>
                <Skeleton width="35%" height="12px" shimmer />
                <Skeleton variant="rounded" width="100%" height="60px" shimmer />
              </div>
            ))}
          </div>
          {/* Options skeleton */}
          <div style={{ marginTop: 12 }}>
            <Skeleton width="30%" height="14px" shimmer />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton key={idx} variant="rounded" width="64px" height="28px" shimmer />
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mobile-ai-storage-content">
        {/* Header con stile PostEditPage */}
        <div className="post-edit-header">
          <button
            className="post-edit-back-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onBack && typeof onBack === 'function') {
                onBack();
              }
            }}
            aria-label={t('postReview.imageEditor.header.backAria')}
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h3>{t('postReview.imageEditor.header.title')}</h3>
          <div className="post-edit-header-spacer"></div>
        </div>

        {/* Current Image Display */}
        {shouldRenderCurrentImage && (
          <>
            <div className="prompt-editor-current-image-container">
              {displayImageUrl ? (
                <img
                  src={displayImageUrl}
                  alt={currentImageName}
                  className="prompt-editor-current-image"
                  onError={(e) => {
                    e.target.src = placeholderImage;
                  }}
                />
              ) : (
                <div className="prompt-editor-current-image placeholder">
                  <div className="prompt-editor-placeholder-content">
                    <img
                      src={iconImage}
                      alt={t('postReview.imageEditor.references.emptyAlt')}
                      className="prompt-editor-placeholder-icon"
                    />
                    <span className="prompt-editor-placeholder-text">
                      {t('postReview.imageEditor.currentImagePlaceholder', { defaultValue: "Genera l'immagine" })}
                    </span>
                  </div>
                </div>
              )}
              {loading && (
                <AILoadingOverlay text={t('postReview.imageEditor.overlay.generating')} />
              )}
            </div>
            <div
              className={`base-image-toggle-card ${useCurrentImageAsBase ? 'active' : ''}`}
              role="group"
              aria-label={t('postReview.imageEditor.base.aria')}
            >
              <div className="base-image-toggle-info">
                <span className="base-image-toggle-title">{t('postReview.imageEditor.base.title')}</span>
                <p className="base-image-toggle-description">
                  {t('postReview.imageEditor.base.description')}
                </p>
              </div>
              <div className="base-image-toggle-status" aria-live="polite">
                <span className={`base-image-toggle-pill ${isModifyMode ? 'on' : 'off'}`}>
                  {baseImageStatusLabel}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Mode Toggle Switch */}
        <div className="prompt-editor-mode-toggle-container">
          <div className="prompt-editor-mode-toggle">
            <button
              className={`prompt-editor-mode-toggle-option ${mode === 'simple' ? 'active' : ''}`}
              onClick={() => setMode('simple')}
            >
              {t('postReview.imageEditor.mode.simple')}
            </button>
            <button
              className={`prompt-editor-mode-toggle-option ${mode === 'pro' ? 'active' : ''}`}
              onClick={() => setMode('pro')}
            >
              {t('postReview.imageEditor.mode.pro')}
            </button>
          </div>
        </div>

        {/* Simple Mode Content */}
        {mode === 'simple' && (
          <div className="prompt-editor-simple-mode">
            <div className="prompt-editor-section">
              <h3 className="prompt-editor-section-title">{t('postReview.imageEditor.simple.title')}</h3>
              <textarea
                className="prompt-editor-simple-textarea"
                value={simplePromptText}
                onChange={(e) => setSimplePromptText(e.target.value)}
                placeholder={t('postReview.imageEditor.simple.placeholder', { defaultValue: 'Es: sostituisci il soggetto con un drago fatto di carta igienica...' })}
                rows={6}
              />
            </div>

            {/* Prompt Images Upload Section */}
            <div className="prompt-editor-section">
              <h3 className="prompt-editor-section-title">{t('postReview.imageEditor.references.title')}</h3>
              <p className="prompt-editor-section-description">
                {t('postReview.imageEditor.references.description')}
              </p>

              {/* Single image display */}
              {promptImage ? (
                <div className="prompt-editor-images-list">
                  <div className="prompt-editor-image-item">
                    <div className="prompt-editor-image-preview">
                      <img
                        src={promptImage.url}
                        alt={promptImage.name}
                        onError={(e) => {
                          e.target.src = placeholderImage;
                        }}
                      />
                    </div>
                    <div className="prompt-editor-image-info">
                      <div className="prompt-editor-image-name">{promptImage.name}</div>
                    </div>
                    <button
                      className="prompt-editor-image-delete"
                      onClick={handleDeletePromptImage}
                      aria-label={t('postReview.imageEditor.references.deleteAria')}
                    >
                      <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt')} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="prompt-editor-images-empty">
                    <div className="prompt-editor-images-empty-icon">
                      <img src={iconImage} alt={t('postReview.imageEditor.references.emptyAlt')} />
                    </div>
                    <p>{t('postReview.imageEditor.references.empty')}</p>
                  </div>

                  {/* Upload actions */}
                  <div className="prompt-editor-upload-actions">
                    <label className="prompt-editor-upload-button">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;

                          const previewUrl = URL.createObjectURL(file);

                          if (promptImageObjectUrlRef.current) {
                            try {
                              URL.revokeObjectURL(promptImageObjectUrlRef.current);
                            } catch (error) {
                              console.warn('[ImagePromptEditorPage] Failed to revoke previous object URL', error);
                            }
                          }

                          promptImageObjectUrlRef.current = previewUrl;

                          setPromptImage({
                            id: previewUrl,
                            url: previewUrl,
                            name: file.name || fallbackReferenceName,
                            file
                          });

                          event.target.value = '';
                        }}
                        hidden
                      />
                      <img src={iconUpload} alt={t('postReview.imageEditor.references.uploadAlt')} className="button-icon" />
                      {t('postReview.imageEditor.references.upload')}
                    </label>
                    <button
                      type="button"
                      className="prompt-editor-upload-button secondary"
                      onClick={triggerPromptImageUpload}
                    >
                      <img src={iconImage} alt={t('postReview.imageEditor.references.galleryAlt')} className="button-icon" />
                      {t('postReview.imageEditor.references.gallery')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Pro Mode Content */}
        {mode === 'pro' && (
          <div className="prompt-editor-pro-mode">
            {/* Blocks editor */}
            <div className="prompt-editor-blocks">
              {blocks.map((block, idx) => {
                const match = /^\s*([^:]{1,40}):\s*(.*)$/.exec(block || '');
                const label = match ? match[1].trim() : t('postReview.imageEditor.blocks.defaultLabel', { defaultValue: 'Blocco' });
                const content = match ? match[2] : (block || '');
                const placeholder = buildBlockPlaceholder(label, idx);
                return (
                  <div className="prompt-block-card" key={`block-${idx}`}>
                    <div className="prompt-block-header">
                      <div className="prompt-block-index">{idx + 1}</div>
                      <div className="prompt-block-label">{label}</div>
                    </div>
                    <textarea
                      className="prompt-block-textarea"
                      value={content}
                      onChange={(e) => handleBlockChange(idx, `${label}: ${e.target.value}`)}
                      placeholder={placeholder}
                      rows={3}
                    />
                  </div>
                );
              })}
            </div>

            {/* Options */}
            <div className="prompt-editor-options">
              <div className="option-card">
                <h3 className="option-card-title">{t('postReview.imageEditor.options.ratiosTitle')}</h3>
                <div className="segmented-control">
                  {[
                    { key: '1:1', label: '1:1' },
                    { key: '16:9', label: '16:9' },
                    { key: '9:16', label: '9:16' },
                    { key: '4:3', label: '4:3' },
                    { key: '3:4', label: '3:4' },
                    { key: '21:9', label: '21:9' },
                    { key: 'custom', label: t('postReview.imageEditor.options.customLabel') },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      className={`segmented-option ${aspectRatio === key ? 'active' : ''}`}
                      onClick={() => setAspectRatio(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {aspectRatio === 'custom' && (
                  <div className="custom-dimensions">
                    <input
                      type="number"
                      className="dimension-input"
                      placeholder={t('postReview.imageEditor.options.widthPlaceholder')}
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      min="256"
                      max="2048"
                    />
                    <span className="dimension-separator">×</span>
                    <input
                      type="number"
                      className="dimension-input"
                      placeholder={t('postReview.imageEditor.options.heightPlaceholder')}
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      min="256"
                      max="2048"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Prompt Images Upload Section */}
            <div className="prompt-editor-section">
              <h3 className="prompt-editor-section-title">{t('postReview.imageEditor.references.title')}</h3>
              <p className="prompt-editor-section-description">
                {t('postReview.imageEditor.references.description')}
              </p>

              {/* Single image display */}
              {promptImage ? (
                <div className="prompt-editor-images-list">
                  <div className="prompt-editor-image-item">
                    <div className="prompt-editor-image-preview">
                      <img
                        src={promptImage.url}
                        alt={promptImage.name}
                        onError={(e) => {
                          e.target.src = placeholderImage;
                        }}
                      />
                    </div>
                    <div className="prompt-editor-image-info">
                      <div className="prompt-editor-image-name">{promptImage.name}</div>
                    </div>
                    <button
                      className="prompt-editor-image-delete"
                      onClick={handleDeletePromptImage}
                      aria-label={t('postReview.imageEditor.references.deleteAria')}
                    >
                      <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt')} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="prompt-editor-images-empty">
                    <div className="prompt-editor-images-empty-icon">
                      <img src={iconImage} alt={t('postReview.imageEditor.references.emptyAlt')} />
                    </div>
                    <p>{t('postReview.imageEditor.references.empty')}</p>
                  </div>

                  {/* Upload actions */}
                  <div className="prompt-editor-upload-actions">
                    <label className="prompt-editor-upload-button">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;

                          const previewUrl = URL.createObjectURL(file);

                          if (promptImageObjectUrlRef.current) {
                            try {
                              URL.revokeObjectURL(promptImageObjectUrlRef.current);
                            } catch (error) {
                              console.warn('[ImagePromptEditorPage] Failed to revoke previous object URL', error);
                            }
                          }

                          promptImageObjectUrlRef.current = previewUrl;

                          setPromptImage({
                            id: previewUrl,
                            url: previewUrl,
                            name: file.name || fallbackReferenceName,
                            file
                          });

                          event.target.value = '';
                        }}
                        hidden
                      />
                      <img src={iconUpload} alt={t('postReview.imageEditor.references.uploadAlt')} className="button-icon" />
                      {t('postReview.imageEditor.references.upload')}
                    </label>
                    <button
                      type="button"
                      className="prompt-editor-upload-button secondary"
                      onClick={triggerPromptImageUpload}
                    >
                      <img src={iconImage} alt={t('postReview.imageEditor.references.galleryAlt')} className="button-icon" />
                      {t('postReview.imageEditor.references.gallery')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="prompt-editor-footer">
          {mode === 'pro' && (
            <button
              type="button"
              className="prompt-editor-regenerate-text"
              onClick={() => setShowPromptBlocksModal(true)}
              disabled={isRegeneratingBlocks || loading}
            >
              {isRegeneratingBlocks ? (
                <>
                  <div className="prompt-editor-spinner" />
                  {t('postReview.imageEditor.footer.regenerateBlocksLoading', { defaultValue: 'Rigenerazione in corso...' })}
                </>
              ) : (
                <>
                  <span className="button-icon">
                    <AIIcon size={18} />
                  </span>
                  {t('postReview.imageEditor.footer.regenerateBlocks', { defaultValue: 'Rigenera blocchi' })}
                </>
              )}
            </button>
          )}

          <button
            className="prompt-editor-generate-image"
            onClick={handleGenerateImage}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="prompt-editor-spinner" />
                {t('postReview.imageEditor.footer.generating')}
              </>
            ) : (
              <>
                <img src={iconImage} alt={t('postReview.imageEditor.footer.iconAlt')} className="button-icon" />
                {t('postReview.imageEditor.footer.generate')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
      <AIRegenerationModal
        isOpen={showPromptBlocksModal}
        onClose={handleClosePromptBlocksModal}
        type="blocks"
        post={post}
        imageIndex={imageIndex}
        onRegenerate={handlePromptBlocksRegeneration}
        isRegenerating={isRegeneratingBlocks}
      />
    </>
  );
};

export default ImagePromptEditorPage;
